import { type PlaylistEntry, trackRouteId } from '@amzn/amp-playback'

/**
 * Catalog / detail-page ids derived from a `PlaylistEntry`'s typed entity.
 *
 * These reproduce the ids the entry builders used to flatten onto the entry
 * (`trackToPlaylistEntry`, `podcastEpisodeToPlaylistEntry`). Deriving them here
 * keeps `PlaylistEntry` down to the entity itself and lets each consumer switch
 * on the entity type.
 *
 * Video entries are the subtle case: the video builders never set these ids
 * even when their entity is a `Track`, so a video `Track` must resolve to null
 * (its detail target is the VideoDetailPage, reached elsewhere). A video
 * *podcast* keeps its episode id — PodcastEpisode links to the show/episode
 * page regardless of video-ness.
 */

/** The catalog track / episode id, or null (video Tracks and audiobooks have none). */
export function entryTrackId(entry: PlaylistEntry): string | null {
    const entity = entry.entity
    if (entity.__typename === 'PodcastEpisode') return entity.id ?? null
    // Prefer the ASIN over the wire `id` (a catalog MRN for a signed-in track)
    // so detail links / lyrics / share resolve to `/tracks/<ASIN>`.
    if (entity.__typename === 'Track' && entry.video !== true) return trackRouteId(entity)
    return null
}

/** The album / podcast-show id, or null. */
export function entryAlbumId(entry: PlaylistEntry): string | null {
    const entity = entry.entity
    if (entity.__typename === 'PodcastEpisode') return entity.podcast?.id ?? null
    if (entity.__typename === 'Track' && entry.video !== true) return entity.album?.id ?? null
    return null
}

/** The primary contributing-artist id, or null (only audio tracks carry one). */
export function entryArtistId(entry: PlaylistEntry): string | null {
    const entity = entry.entity
    if (entity.__typename === 'Track' && entry.video !== true) {
        return entity.contributingArtists?.edges[0]?.node.id ?? null
    }
    return null
}

/** The page the entry's title opens: track or podcast episode, an ad's advertiser, else null (video, station, playlist). */
export function entryDetailUrl(entry: PlaylistEntry | null): string | null {
    if (!entry) return null
    const entity = entry.entity
    if (entity.__typename === 'Advertisement') return entity.url || null
    const typename = entity.__typename
    if (typename === 'PodcastEpisode') {
        const showId = entryAlbumId(entry)
        const episodeId = entryTrackId(entry)
        return showId && episodeId ? `/podcasts/${showId}/episodes/${episodeId}` : null
    }
    const trackId = entryTrackId(entry)
    return typename === 'Track' && trackId ? `/tracks/${trackId}` : null
}
